import axios from 'axios'

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error)
        } else {
            prom.resolve(token)
        }
    })
    failedQueue = []
}

export const refreshAccessToken = async () => {
    try {
        const refreshToken = localStorage.getItem('refreshToken')
        console.log('[REFRESH] Step 1: Retrieved refresh token from localStorage:', refreshToken ? 'EXISTS' : 'NOT FOUND')

        if (!refreshToken) {
            console.error('[REFRESH] Step 1 FAILED: No refresh token available')
            throw new Error('No refresh token available')
        }

        const refreshUrl = process.env.REACT_APP_NEW_BASE_V4_URL_AUTH + 'refresh'
        console.log('[REFRESH] Step 2: Calling refresh endpoint:', refreshUrl)
        console.log('[REFRESH] Step 2: Request body:', { refreshToken: refreshToken.substring(0, 20) + '...' })

        const response = await axios.post(refreshUrl, { refreshToken })

        console.log('[REFRESH] Step 3: Refresh response received:', response.status)
        console.log('[REFRESH] Step 3: Response data:', response.data)

        const { accessToken, refreshToken: newRefreshToken } = response.data

        if (!accessToken) {
            console.error('[REFRESH] Step 3 FAILED: No accessToken in response:', response.data)
            throw new Error('No access token in refresh response')
        }

        localStorage.setItem('accessToken', accessToken)
        console.log('[REFRESH] Step 4: Saved new accessToken to localStorage')

        if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken)
            console.log('[REFRESH] Step 4: Saved new refreshToken to localStorage')
        }

        console.log('[REFRESH] SUCCESS: Token refresh completed successfully')
        return accessToken
    } catch (error) {
        console.error('[REFRESH] FAILED: Error during token refresh:', error)
        console.error('[REFRESH] Error response:', error.response?.data)
        console.error('[REFRESH] Error status:', error.response?.status)

        // // Show alert so user knows what's happening
        // alert('Token refresh failed. You will be logged out in 5 seconds. Check console for details.')

        // // Wait 5 seconds so user can see console logs
        // await new Promise(resolve => setTimeout(resolve, 5000))

        // Clear all tokens and redirect to login
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('userType')
        localStorage.removeItem('userId')
        localStorage.removeItem('Authorised')

        console.log('[REFRESH] Redirecting to login...')
        window.location.href = '/login'
        throw error
    }
}

// Create the centralized axios instance
export const axiosClient = axios.create()

// Request interceptor - add token to all requests
axiosClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => Promise.reject(error)
)

// Response interceptor - handle 401 with token refresh
axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config

        if (error.response?.status === 401 && !originalRequest._retry) {
            console.log('[401 INTERCEPTOR] Caught 401 error for:', originalRequest.url)
            console.log('[401 INTERCEPTOR] Is already refreshing?:', isRefreshing)

            if (isRefreshing) {
                console.log('[401 INTERCEPTOR] Already refreshing, adding to queue')
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                })
                    .then((token) => {
                        console.log('[401 INTERCEPTOR] Queue resolved with new token, retrying request')
                        originalRequest.headers['Authorization'] = 'Bearer ' + token
                        return axiosClient(originalRequest)
                    })
                    .catch((err) => {
                        console.error('[401 INTERCEPTOR] Queue rejected:', err)
                        return Promise.reject(err)
                    })
            }

            originalRequest._retry = true
            isRefreshing = true
            console.log('[401 INTERCEPTOR] Starting token refresh...')

            try {
                const accessToken = await refreshAccessToken()
                console.log('[401 INTERCEPTOR] Token refresh successful, updating headers')
                axiosClient.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken
                originalRequest.headers['Authorization'] = 'Bearer ' + accessToken
                processQueue(null, accessToken)
                console.log('[401 INTERCEPTOR] Retrying original request:', originalRequest.url)
                return axiosClient(originalRequest)
            } catch (err) {
                console.error('[401 INTERCEPTOR] Token refresh failed:', err)
                processQueue(err, null)
                return Promise.reject(err)
            } finally {
                isRefreshing = false
                console.log('[401 INTERCEPTOR] Refresh process complete')
            }
        }

        return Promise.reject(error)
    }
)
